import { ErrorMessage } from "@components/atoms/ErrorMessage"
import { Button } from "@components/controls/Button"
import { Field } from "@components/controls/Field"
import { TextInput } from "@components/controls/TextInput"
import { zodResolver } from "@hookform/resolvers/zod"
import { useLogin } from "@hooks/queries"
import { useSchemaPref } from "@hooks/usePref"
import { Link, useRoute } from "eddev/routing"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { useAuth } from "../hooks/useAuth"
import { AUTH_ERROR_MESSAGES } from "../utils/constants"
import { MakeURL } from "@utils/create-url"
import { Tabs } from "@components/controls/Tabs"
import { useEffect } from "react"

const loginSchemaEmail = z.object({
  login: z.string().email(),
  password: z.string().min(4, "Please enter your password"),
})

const loginSchemaNumber = z.object({
  login: z.string().regex(/^[0-9]+$/, "This format is invalid"),
  password: z.string().min(4, "Please enter your password"),
})

type LoginSchema = z.infer<typeof loginSchemaEmail>

type Props = {
  onSuccess?: () => void
  loginReason?: string
  disableSignup?: boolean
}

export function LoginForm(props: Props) {
  const route = useRoute()
  const redirect = route.view === "login" ? (route.query.redirect as string) : route.pathname
  const auth = useAuth()

  const [loginMode, setLoginMode] = useSchemaPref("login", z.enum(["number", "email"]).default("number"))

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginSchema>({ resolver: zodResolver(loginMode === "email" ? loginSchemaEmail : loginSchemaNumber) })

  const login = useLogin({
    onSuccess(data) {
      auth.handleLoggedIn({
        authToken: data.login?.authToken!,
        refreshToken: data.login?.refreshToken!,
        name: data.login?.customer?.firstName!,
        email: data.login?.customer?.email!,
      })
      setTimeout(() => {
        if (props.onSuccess) {
          props.onSuccess()
        }
      }, 500)
    },
    onError(err) {
      if (err.message in AUTH_ERROR_MESSAGES) {
        setError("root", { message: AUTH_ERROR_MESSAGES[err.message] })
      }
    },
  })

  // Automatically swap between email/number, depending on the current value
  // This will be useful with autocomplete, or if the user starts typing
  useEffect(() => {
    const sub = watch((value, { name }) => {
      if (name === "login") {
        if (value.login?.length! > 4) {
          if (value.login?.match(/^[a-z\.\-\_]+/)) {
            setLoginMode("email")
          } else if (value.login?.match(/^[0-9]+$/)) {
            setLoginMode("number")
          }
        }
      }
    })
    return () => sub.unsubscribe()
  }, [])

  return (
    <form
      noValidate
      onSubmit={handleSubmit(async (data) => {
        await login.mutateAsync({
          username: data.login,
          password: data.password,
        })
      })}
    >
      <Tabs
        items={[
          {
            label: "Member No.",
            value: "number",
          },
          {
            label: "Email",
            value: "email",
          },
        ]}
        value={loginMode}
        onValueChange={setLoginMode}
        swapOrder
        className="mb-4"
      />
      <ErrorMessage>
        {errors.root?.message ? (
          <>
            <p>{errors.root?.message}</p>
            <p className="mt-3">
              <strong>
                Please note that users who registered before 16 October 2024 will need to <Link href="/auth/login?mode=forgot">reset their password</Link>.
              </strong>
            </p>
          </>
        ) : null}
      </ErrorMessage>
      {!!props.loginReason && <div className="my-4 type-body-m-roman">{props.loginReason}</div>}
      <div className="stack-y-5 pt-4">
        <Field label={loginMode === "email" ? "Email" : "Membership Number"} required error={errors.login?.message}>
          <TextInput type="text" inputMode={loginMode === "email" ? "email" : "numeric"} {...register("login")} className="w-full" />
        </Field>
        <Field label="Password" required error={errors.password?.message}>
          <TextInput type="password" {...register("password")} className="w-full" />
        </Field>
        <div className="stack-x-2">
          {props.disableSignup !== true && (
            <Button href={MakeURL.join()} type="button" variant="outline" size="default">
              Become a member
            </Button>
          )}
          <Button loading={isSubmitting} type="submit">
            Sign in
          </Button>
        </div>
      </div>
      <div className="pt-6">
        <Link className="isolated-link" href={MakeURL.resetPassword(redirect)}>
          Forgot your password?
        </Link>
      </div>
    </form>
  )
}
